import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiError, GetMenu } from '../../apis/auth';
import { MenuItemData } from '@/apis/types';
import { clearAllStorageAndState } from '@/common/helper/storageCleanup';

export interface FeatureData {
  id?: string;
  name?: string;
  description?: string;
  enabled?: boolean;
  [key: string]: unknown;
}

export interface MenuState {
  items: MenuItemData[];
  features: FeatureData[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
}

export const fetchMenu = createAsyncThunk<
  { items: MenuItemData[]; features: FeatureData[] },
  string,
  { rejectValue: string }
>('menu/fetchMenu', async (token: string, { rejectWithValue }) => {
  try {
    const res = await GetMenu(token);
    const data = res?.data?.menus ? res?.data?.menus : null;
    const features =
      (res?.data as { features?: FeatureData[] })?.features || [];
    if (data && res?.status === 200) {
      const menuItems = data?.length > 0 ? data : [];
      if (menuItems.length === 0) {
        return rejectWithValue('NO_MENU_ITEMS');
      }
      return { items: menuItems, features };
    } else {
      clearAllStorageAndState({ broadcast: true, navigateToLogin: true });
      return rejectWithValue('Failed to fetch menu: Success flag not true');
    }
  } catch (error) {
    clearAllStorageAndState({ broadcast: true, navigateToLogin: true });
    console.error('Error fetching menu:', error);
    const apiError = error as ApiError;
    return rejectWithValue(
      apiError.response?.data?.message || 'Failed to fetch menu'
    );
  }
});

const initialState: MenuState = {
  items: [],
  features: [],
  status: 'idle',
};

/**
 * Redux slice for menu state management
 * Handles fetching and storing menu items from the API
 */
const menuSlice = createSlice({
  name: 'menu',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchMenu.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchMenu.fulfilled, (state, action) => {
        state.items = action.payload.items;
        state.features = action.payload.features;
        state.status = 'succeeded';
      })
      .addCase(fetchMenu.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export default menuSlice.reducer;
